import { Schema } from 'effect';

import { DateTimeSerializableFromString } from '../DateTimeSerializableFromString';
import { overrideSchema } from '../openapi/overrideSchema';

const JsonLdContext = Schema.optionalKey(
  Schema.Union(
    [
      Schema.String,
      Schema.Struct({
        '@vocab': Schema.String,
        hydra: Schema.Literal('http://www.w3.org/ns/hydra/core#'),
      }),
    ],
    { mode: 'oneOf' }
  ).annotate({ readOnly: true })
);
const JsonLdIri = Schema.optionalKey(Schema.String.annotate({ readOnly: true }));
const JsonLdType = Schema.optionalKey(Schema.String.annotate({ readOnly: true }));
const NullableString = Schema.Union([Schema.String, Schema.Null]);

const CustomerAddressBase = Schema.Struct({
  '@context': JsonLdContext,
  '@id': JsonLdIri,
  '@type': JsonLdType,
  vatNumber: NullableString,
  businessId: NullableString.annotate({ description: 'IČO or similar ID for other countries' }),
  id: Schema.optionalKey(Schema.Finite.annotate({ readOnly: true }).check(Schema.isInt())),
  firstName: Schema.String,
  lastName: Schema.String,
  phoneNumber: NullableString,
  company: NullableString,
  countryCode: Schema.String,
  provinceCode: NullableString,
  provinceName: NullableString,
  street: Schema.String,
  city: Schema.String,
  postcode: Schema.String,
});

const CustomerShowUserBase = Schema.Struct({
  '@context': JsonLdContext,
  '@id': JsonLdIri,
  '@type': JsonLdType,
  verified: Schema.optionalKey(Schema.Boolean.annotate({ readOnly: true })),
});

/** Branded customer email — route and form boundaries can construct it with `.make()`. */
export const CustomerEmail = Schema.String.pipe(Schema.brand('Customer.email'));
export type CustomerEmail = typeof CustomerEmail.Type;

const CustomerIri = Schema.optionalKey(
  Schema.String.annotate({ readOnly: true }).pipe(Schema.brand('Customer.iri'))
);
const CustomerId = Schema.optionalKey(
  Schema.Finite.annotate({ readOnly: true }).check(Schema.isInt()).pipe(Schema.brand('Customer.id'))
);
const CustomerFirstName = Schema.Union([
  Schema.String.pipe(Schema.brand('Customer.firstName')),
  Schema.Null,
]);
const CustomerLastName = Schema.Union([
  Schema.String.pipe(Schema.brand('Customer.lastName')),
  Schema.Null,
]);
const CustomerPhoneNumber = Schema.Union([
  Schema.String.pipe(Schema.brand('Customer.phoneNumber')),
  Schema.Null,
]);
const CustomerFullName = Schema.optionalKey(
  Schema.String.annotate({ readOnly: true }).pipe(Schema.brand('Customer.fullName'))
);

export const CustomerMeBase = Schema.Struct({
  '@context': JsonLdContext,
  '@id': JsonLdIri,
  '@type': JsonLdType,
  defaultAddress: Schema.Union([CustomerAddressBase, Schema.Null]),
  addresses: Schema.Array(CustomerAddressBase),
  user: Schema.Union([Schema.String.annotate({ format: 'iri-reference' }), Schema.Null]),
  id: Schema.optionalKey(Schema.Finite.annotate({ readOnly: true }).check(Schema.isInt())),
  email: Schema.String,
  firstName: NullableString,
  lastName: NullableString,
  birthday: Schema.Union([DateTimeSerializableFromString, Schema.Null]),
  gender: Schema.String.annotate({ default: 'u' }),
  phoneNumber: NullableString,
  subscribedToNewsletter: Schema.Boolean.annotate({ default: false }),
  fullName: Schema.optionalKey(Schema.String.annotate({ readOnly: true })),
});

export const CustomerShowBase = Schema.Struct({
  '@context': JsonLdContext,
  '@id': JsonLdIri,
  '@type': JsonLdType,
  defaultAddress: Schema.Union([Schema.String.annotate({ format: 'iri-reference' }), Schema.Null]),
  user: Schema.Union([CustomerShowUserBase, Schema.Null]),
  email: Schema.String,
  firstName: NullableString,
  lastName: NullableString,
  birthday: Schema.Union([DateTimeSerializableFromString, Schema.Null]),
  gender: Schema.String.annotate({ default: 'u' }),
  phoneNumber: NullableString,
  subscribedToNewsletter: Schema.Boolean.annotate({ default: false }),
  fullName: Schema.optionalKey(Schema.String.annotate({ readOnly: true })),
});

export const CustomerMe = overrideSchema(CustomerMeBase, {
  '@id': CustomerIri,
  id: CustomerId,
  email: CustomerEmail,
  firstName: CustomerFirstName,
  lastName: CustomerLastName,
  phoneNumber: CustomerPhoneNumber,
  fullName: CustomerFullName,
}).annotate({ identifier: 'CustomerMe' });
export type CustomerMe = Schema.Schema.Type<typeof CustomerMe>;

export const CustomerShow = overrideSchema(CustomerShowBase, {
  '@id': CustomerIri,
  email: CustomerEmail,
  firstName: CustomerFirstName,
  lastName: CustomerLastName,
  phoneNumber: CustomerPhoneNumber,
  fullName: CustomerFullName,
}).annotate({ identifier: 'CustomerShow' });
export type CustomerShow = Schema.Schema.Type<typeof CustomerShow>;
